package pl.nickers.match3

/**
 * Business logic for Journey mode progression. Has no Compose/UI imports — fully unit-testable.
 *
 * The [levelLoader] lambda is injected so that tests can supply in-memory level data
 * without touching the file system.
 */
class JourneyService(
    private val catalog: JourneyCatalog,
    private val storage: GameStorage,
    private val levelLoader: suspend (JourneyEntry) -> LevelConfig,
) {
    val entries: List<JourneyEntry> get() = catalog.entries

    val highestUnlockedLevel: Int get() = storage.highestUnlockedLevel

    fun isUnlocked(levelId: Int): Boolean = levelId <= highestUnlockedLevel

    fun entryFor(levelId: Int): JourneyEntry? = catalog.entryFor(levelId)

    suspend fun loadLevel(levelId: Int): LevelConfig {
        val entry = catalog.entryFor(levelId) ?: error("Level $levelId not found")
        return levelLoader(entry)
    }

    /** Advances the highest unlocked level to [completedLevelId] + 1, if not already higher. */
    fun unlockNextLevel(completedLevelId: Int) {
        val next = completedLevelId + 1
        if (next > storage.highestUnlockedLevel) {
            storage.highestUnlockedLevel = next
        }
    }

    fun isLastLevel(levelId: Int): Boolean = catalog.entries.none { it.id > levelId }

    // ------------------------------------------------------------------
    // Star ratings (F9.2 / F9.5)
    // ------------------------------------------------------------------

    /** Best star rating (0–3) earned for [levelId]. 0 for locked/unplayed levels. */
    fun starsForLevel(levelId: Int): Int = storage.starsForLevel(levelId)

    /** Total stars earned across the whole journey. */
    val totalStars: Int get() = catalog.entries.sumOf { storage.starsForLevel(it.id) }

    /** Maximum possible stars across the whole journey (3 per level). */
    val maxStars: Int get() = catalog.entries.size * 3

    /** Stars earned for a *winning* run of [config] that scored [score]. Winning always earns ≥1. */
    fun starsForWin(score: Int, config: LevelConfig): Int =
        starsForScore(score, config.effectiveStarThresholds).coerceAtLeast(1)

    /**
     * Records [stars] for [levelId], raising the stored best only (replays never downgrade).
     * [stars] is clamped to the valid 0–3 range before comparison/persistence, so an
     * out-of-range caller can never store a value that would break the header math or
     * render oddly. Returns `true` when the stored value increased — i.e. this run set a
     * new personal best.
     */
    fun recordLevelStars(levelId: Int, stars: Int): Boolean {
        val clamped = stars.coerceIn(0, 3)
        val previous = storage.starsForLevel(levelId)
        if (clamped > previous) {
            storage.setStarsForLevel(levelId, clamped)
            return true
        }
        return false
    }

    /**
     * Resets journey progress back to the beginning (level 1 unlocked only), clears any
     * in-progress saved game and star ratings, and clears the "continue" pointer so the
     * Start screen no longer offers to resume a now-reset level.
     */
    fun resetProgress() {
        storage.highestUnlockedLevel = 1
        storage.clearSavedGame()
        storage.continueContext = null
        storage.clearAllStars()
    }

    companion object {
        /** Production factory: loads the index from resources and wires file-based level loading. */
        suspend fun create(storage: GameStorage): JourneyService {
            val catalog = JourneyCatalog.load()
            return JourneyService(catalog, storage) { entry -> catalog.loadLevel(entry) }
        }
    }
}

/**
 * Pure score → star mapping: how many of the (ascending) [thresholds] the [score]
 * meets-or-exceeds, clamped to 0..3. Has no "win floor" — call
 * [JourneyService.starsForWin] for the winning-always-earns-≥1 rule.
 *
 * Boundary contract: a score exactly equal to a threshold counts as meeting it
 * (`score >= threshold`), so a score of `s1` earns 1 star, `s2` earns 2, `s3` earns 3.
 */
fun starsForScore(score: Int, thresholds: List<Int>): Int =
    thresholds.count { score >= it }.coerceAtMost(3)
